/**
 * ChatBot — natural-language shipment intake.
 * Extracts shipment fields from free-form chat. When complete, shows a preview card
 * with a button that pre-fills the Single Shipment form (via onShipmentProposed callback).
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001'}/api`;

const INITIAL_GREETING = {
  role: 'assistant',
  content:
    "Hi! I'll help you set up a shipment. To calculate emissions I'll need 6 things:\n" +
    "  • what you're shipping\n" +
    "  • quantity & unit (e.g. 100 kg)\n" +
    "  • origin city\n" +
    "  • destination city\n" +
    "  • transport mode (road/rail/air/water)\n\n" +
    "Tell me what you have — I'll ask for the rest.",
};

/**
 * @param {object} props
 * @param {function} [props.onShipmentProposed] Cat 4/9 freight proposal handler
 * @param {function} [props.onScope3Proposed]  Cat 6/7 activity proposal handler
 * @param {4|6|7|9} [props.category]           Drives which endpoint + tool the AI uses
 */
export default function ChatBot({ onShipmentProposed, onScope3Proposed, category = 4 }) {
  const [open, setOpen] = useState(false);
  // turns: [{role, content, shipment?, activity?}, ...]
  const [turns, setTurns] = useState([INITIAL_GREETING]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const isScope3 = category === 6 || category === 7;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, sending, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setError(null);

    const next = [...turns, { role: 'user', content: trimmed }];
    setTurns(next);
    setInput('');
    setSending(true);

    // Backend wants {role, content} only; drop the synthetic greeting + any local-only fields.
    const apiHistory = next
      .filter((m, i) => !(i === 0 && m === INITIAL_GREETING))
      .map((m) => ({ role: m.role, content: m.content }));
    try {
      const endpoint = isScope3 ? `${API}/chat/scope3` : `${API}/chat`;
      const res = await axios.post(endpoint, { messages: apiHistory });
      setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.data.reply,
          shipment: res.data.shipment || null,
          activity: res.data.activity || null,
        },
      ]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        err.response?.status === 401
          ? 'Please log in again to use the assistant.'
          : detail || 'Could not reach the assistant. Please try again.';
      setError(msg);
      setTurns((prev) => prev.slice(0, -1));
      setInput(trimmed);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const useProposal = (shipment) => {
    if (!onShipmentProposed) return;
    onShipmentProposed(shipment);
    setOpen(false);
  };

  const useActivity = (activity) => {
    if (!onScope3Proposed) return;
    onScope3Proposed(activity);
    setOpen(false);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open CarbonRoute Assistant"
          style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 9999 }}
          className="w-14 h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div
          style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 9999 }}
          className="w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-semibold leading-tight">CarbonRoute Assistant</div>
                <div className="text-[11px] opacity-80 leading-tight">Describe a shipment or ask a question</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-gray-50">
            {turns.map((m, i) => (
              <div key={i} className="space-y-2">
                <div className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                      m.role === 'user'
                        ? 'bg-primary text-primary-foreground rounded-br-sm'
                        : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
                {m.activity && (
                  <div className="flex justify-start">
                    <div className="max-w-[90%] w-full bg-white border border-emerald-200 rounded-xl p-3 shadow-sm">
                      <div className="flex items-center gap-1.5 mb-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs font-semibold text-gray-700">
                          Inferred Scope 3 Cat {m.activity.ghg_category} Activity
                        </span>
                      </div>
                      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <dt className="text-gray-500">Mode</dt>
                        <dd className="text-gray-800 font-medium capitalize">{m.activity.mode}</dd>
                        {m.activity.activity_value != null && (
                          <>
                            <dt className="text-gray-500">Amount</dt>
                            <dd className="text-gray-800 font-medium">{m.activity.activity_value} {m.activity.activity_unit}</dd>
                          </>
                        )}
                        {m.activity.origin && (
                          <>
                            <dt className="text-gray-500">From</dt>
                            <dd className="text-gray-800 font-medium truncate">{m.activity.origin}</dd>
                          </>
                        )}
                        {m.activity.destination && (
                          <>
                            <dt className="text-gray-500">To</dt>
                            <dd className="text-gray-800 font-medium truncate">{m.activity.destination}</dd>
                          </>
                        )}
                        {m.activity.cabin_class && (
                          <>
                            <dt className="text-gray-500">Cabin</dt>
                            <dd className="text-gray-800 font-medium">{m.activity.cabin_class}</dd>
                          </>
                        )}
                        {m.activity.fuel_type && (
                          <>
                            <dt className="text-gray-500">Fuel</dt>
                            <dd className="text-gray-800 font-medium capitalize">{m.activity.fuel_type}</dd>
                          </>
                        )}
                      </dl>
                      <button
                        onClick={() => useActivity(m.activity)}
                        className="mt-3 w-full py-2 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                      >
                        Open in Scope 3 form
                      </button>
                    </div>
                  </div>
                )}
                {m.shipment && (
                  <div className="flex justify-start">
                    <div className="max-w-[90%] w-full bg-white border border-primary/30 rounded-xl p-3 shadow-sm">
                      <div className="flex items-center gap-1.5 mb-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-xs font-semibold text-gray-700">Inferred Shipment</span>
                      </div>
                      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <dt className="text-gray-500">Goods</dt>
                        <dd className="text-gray-800 font-medium truncate">{m.shipment.good_name}</dd>
                        <dt className="text-gray-500">Quantity</dt>
                        <dd className="text-gray-800 font-medium">{m.shipment.quantity} {m.shipment.unit}</dd>
                        <dt className="text-gray-500">From</dt>
                        <dd className="text-gray-800 font-medium truncate">{m.shipment.origin}</dd>
                        <dt className="text-gray-500">To</dt>
                        <dd className="text-gray-800 font-medium truncate">{m.shipment.destination}</dd>
                        <dt className="text-gray-500">Mode</dt>
                        <dd className="text-gray-800 font-medium capitalize">{m.shipment.transport_mode}</dd>
                        <dt className="text-gray-500">Vehicle</dt>
                        <dd className="text-gray-800 font-medium truncate" title={m.shipment.vehicle_type}>
                          {m.shipment.vehicle_type}
                        </dd>
                        <dt className="text-gray-500">GHG Cat</dt>
                        <dd className="text-gray-800 font-medium capitalize">{m.shipment.ghg_category}</dd>
                        {m.shipment.cost > 0 && (
                          <>
                            <dt className="text-gray-500">Cost</dt>
                            <dd className="text-gray-800 font-medium">₹{m.shipment.cost}</dd>
                          </>
                        )}
                      </dl>
                      <button
                        onClick={() => useProposal(m.shipment)}
                        className="mt-3 w-full py-2 text-xs font-semibold rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground transition-colors"
                      >
                        Open in Single Shipment
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Thinking…
                </div>
              </div>
            )}
            {error && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 bg-white p-2">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="e.g., 100 kg of cotton, Chennai to Bangalore by truck"
                rows={1}
                disabled={sending}
                className="flex-1 resize-none px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent max-h-24 disabled:bg-gray-50"
              />
              <button
                onClick={send}
                disabled={!input.trim() || sending}
                className="w-9 h-9 rounded-lg bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground text-primary-foreground flex items-center justify-center transition-colors flex-shrink-0"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[10px] text-gray-400 mt-1 px-1">
              Enter to send · Shift+Enter for new line
            </div>
          </div>
        </div>
      )}
    </>
  );
}
